import {Trans} from '@lingui/macro';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {collectEnumValues} from '../../../../../libs/shared/helpers/src/typescript/enumHelpers';
import {Toggle} from '../../../../../libs/shared/ui/src/components/booleanInputs/toggle';
import {DropdownBoxCoordinator} from '../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {TextAreaBox} from '../../../../../libs/shared/ui/src/components/boxedInputs/textAreaBox';
import {SimpleDropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown';
import {SimpleDropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdownItem';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {useClipboard} from '../../../../helpers/hooks/clipboardHookHelpers';
import {isTeamNamespace} from '../../../../helpers/models/namespaceHelpers';
import {ChannelFrontChatModel} from '../../../../models/api/channelModel';
import {ChatPlacementLocationsEnum} from '../../../../models/api/channelPreferencesModel';
import {SettingsInboxesUrl} from '../../../../models/url/settingsUrlModel';
import {SettingsFieldLine} from '../../../common/settingsFieldLine';
import {SettingsFieldsSectionTitle} from '../../../common/settingsFieldsSectionTitle';
import {StyledSettingsFormField, StyledSettingsFormTextBox} from '../../../common/settingsWrapper';
import {
  ChannelAutoAssignField,
  ChannelReplyControlField,
  ChannelUndoSendTimingField,
  SettingsCommonChannelFieldProps,
} from '../settingsCommonChannelFields';
import {SettingsChatWidgetColor} from './settingsChatWidgetColor';
import {SettingsChatWidgetLogo} from './settingsChatWidgetLogo';
import {SettingsOfflineHoursField} from './settingsOfflineHoursField';
import {SettingsWelcomeMessageField} from './settingsWelcomeMessageField';

/*
 * Props.
 */

interface SettingsFrontChatFieldsProps extends SettingsCommonChannelFieldProps<ChannelFrontChatModel> {
  url: SettingsInboxesUrl;
}

/*
 * Style.
 */

const StyledTextAreaWrap = styled.div`
  max-width: 600px;
  width: 100%;
`;

const StyledCodeSnippetTextArea = styled(TextAreaBox)`
  width: 100%;
  height: 110px;
`;

/*
 * Component.
 */

export const SettingsFrontChatFields: FC<SettingsFrontChatFieldsProps> = (props) => {
  const {onPreferencesChange, onChange, edited, setDirty} = props;
  const {copy} = useClipboard();
  const isSharedChannel = isTeamNamespace(props.edited.namespace);

  return (
    <>
      <div>
        <SettingsFieldsSectionTitle>
          <Trans id="settingschannel-reply">Reply settings</Trans>
        </SettingsFieldsSectionTitle>
        <ChannelUndoSendTimingField
          edited={edited}
          onChange={onChange}
          onPreferencesChange={onPreferencesChange}
          setDirty={setDirty}
        />
        {isSharedChannel && (
          <ChannelReplyControlField
            edited={edited}
            onChange={onChange}
            onPreferencesChange={onPreferencesChange}
            setDirty={setDirty}
          />
        )}
        <ChannelAutoAssignField
          edited={edited}
          onChange={onChange}
          onPreferencesChange={onPreferencesChange}
          setDirty={setDirty}
        />
      </div>
      <div>
        <SettingsFieldsSectionTitle>
          <Trans id="settingschannel-chatwidgetstyle">Chat widget style</Trans>
        </SettingsFieldsSectionTitle>
        <SettingsChatWidgetColor
          edited={edited}
          onChange={onChange}
          onPreferencesChange={onPreferencesChange}
          setDirty={setDirty}
        />
        <SettingsChatWidgetLogo
          edited={edited}
          onChange={onChange}
          onPreferencesChange={onPreferencesChange}
          setDirty={setDirty}
        />
        <SettingsFieldLine
          title={<Trans id="settingschannel-headers">Headings</Trans>}
          hasVerticalLayout
          content={
            <div>
              <StyledSettingsFormField>
                <StyledSettingsFormTextBox
                  $width={224}
                  size={VisualSizesEnum.MEDIUM}
                  label={<Trans id="channeledited-heading">Heading</Trans>}
                  value={edited.preferences.widgetHeading}
                  onChange={(newName) => {
                    onPreferencesChange({
                      widgetHeading: newName,
                    });
                  }}
                />
              </StyledSettingsFormField>
              <StyledSettingsFormField>
                <StyledSettingsFormTextBox
                  $width={224}
                  size={VisualSizesEnum.MEDIUM}
                  label={<Trans id="channeledited-subheading">Subheading</Trans>}
                  value={edited.preferences.widgetSubHeading}
                  onChange={(newName) => {
                    onPreferencesChange({
                      widgetSubHeading: newName,
                    });
                  }}
                />
              </StyledSettingsFormField>
            </div>
          }
        />
        <SettingsFieldLine
          title={<Trans id="settingschannel-placement-title">Chat widget placement</Trans>}
          subtitle={
            <Trans id="settingschannel-placement-subtitle">
              Decide the position of the chat widget on your website
            </Trans>
          }
          hasVerticalLayout
          content={
            <div>
              <StyledSettingsFormField>
                <DropdownBoxCoordinator
                  label={<Trans id="settingschannel-widgetposition">Chat widget position</Trans>}
                  value={chatPlacementsRenderers[edited.preferences.widgetPlacement.widgetPlacementLocation]}
                  renderDropdown={(popoverProps) => (
                    <SimpleDropdown {...popoverProps}>
                      {collectEnumValues(ChatPlacementLocationsEnum).map((value) => (
                        <SimpleDropdownItem
                          key={value}
                          isChecked={edited.preferences.widgetPlacement.widgetPlacementLocation === value}
                          onSelect={() => {
                            onPreferencesChange({
                              widgetPlacement: {
                                ...edited.preferences.widgetPlacement,
                                widgetPlacementLocation: value,
                              },
                            });
                          }}
                        >
                          {chatPlacementsRenderers[value]}
                        </SimpleDropdownItem>
                      ))}
                    </SimpleDropdown>
                  )}
                />
              </StyledSettingsFormField>
            </div>
          }
        />
      </div>
      <div>
        <SettingsFieldsSectionTitle>
          <Trans id="settingschannel-widgetdisplaydetails">Chat widget display details</Trans>
        </SettingsFieldsSectionTitle>
        <SettingsWelcomeMessageField
          edited={props.edited}
          onChange={props.onChange}
          onPreferencesChange={props.onPreferencesChange}
          setDirty={props.setDirty}
        />
        <SettingsOfflineHoursField
          edited={props.edited}
          onChange={props.onChange}
          onPreferencesChange={props.onPreferencesChange}
          setDirty={props.setDirty}
        />
      </div>
      <div>
        <SettingsFieldsSectionTitle>
          <Trans id="settingschannel-verify">Verify logged-in user identity</Trans>
        </SettingsFieldsSectionTitle>
        <SettingsFieldLine
          title={<Trans id="settingschannel-verificationsecret-title">Verification secret</Trans>}
          subtitle={
            <Trans id="settingschannel-verificationsecret-subtitle">
              Use this verification secret to pass user names and email addresses to Front
            </Trans>
          }
          hasVerticalLayout
          content={
            <StyledSettingsFormTextBox
              $width={258}
              size={VisualSizesEnum.MEDIUM}
              onCopy={() => {
                copy(edited.preferences.verifiedIdentitySecret);
              }}
              label={<Trans id="settingschannel-verificationsecret">Verification secret</Trans>}
              value={edited.preferences.verifiedIdentitySecret}
            />
          }
        />
        <SettingsFieldLine
          title={
            <Trans id="settingschannel-enforceidentityverification-title">
              Only accept messages from verified users
            </Trans>
          }
          subtitle={
            <Trans id="settingschannel-enforceidentityverification-subtitle">
              Enable this option to reject messages from unverified users
            </Trans>
          }
          content={
            <Toggle
              isChecked={props.edited.preferences.enforceIdentityVerification}
              onChange={(v) => {
                props.onPreferencesChange({
                  enforceIdentityVerification: v,
                });
              }}
            />
          }
        />
      </div>
      <div>
        <SettingsFieldsSectionTitle>
          <Trans id="settingschannel-install">Install chat widget</Trans>
        </SettingsFieldsSectionTitle>
        <SettingsFieldLine
          title={<Trans id="settingschannel-install-title">Code snippet</Trans>}
          subtitle={
            <Trans id="settingschannel-install-subtitle">
              Copy this code snippet and insert it into your page right before the &lt;/body&gt; tag. Learn
              more.
            </Trans>
          }
          hasVerticalLayout
          content={
            <StyledTextAreaWrap>
              <StyledCodeSnippetTextArea
                value={`<script src="https://chat-assets.frontapp.com/v1/chat.bundle.js"></script>
              <script>
                window.FrontChat('init', {chatId: '${edited.preferences.widgetSettingsPath}', useDefaultLauncher: true});
              </script>`}
              />
            </StyledTextAreaWrap>
          }
        />
      </div>
    </>
  );
};

const chatPlacementsRenderers: Record<ChatPlacementLocationsEnum, ReactNode> = {
  [ChatPlacementLocationsEnum.BOTTOM_LEFT]: <Trans id="chatplacement-bottom-left">Bottom left</Trans>,
  [ChatPlacementLocationsEnum.BOTTOM_RIGHT]: <Trans id="chatplacement-bottom-right">Bottom right</Trans>,
};
